import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { createUseStyles } from 'react-jss';

import { Divider, Button, Tooltip } from 'antd';
import { ArrowLeftOutlined, LikeOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/fr';
import { commentsSelector, postsSelector, store } from 'src/store';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';

import {
  Post,
  fetchPostByPostId,
  removeAllPosts,
  addLikeToPost,
  removeLikeOfPost,
} from 'src/slices/post';
import { Tag, removeAllTags } from 'src/slices/tag';
import { User, fetchCurrentUser, findUsersByIds } from 'src/slices/auth';
import {
  Comment,
  fetchCommentsByPostId,
  removeAllComments,
} from 'src/slices/comment';
import { urlDomainName } from '../utils/isValidUrl';

import { CustomTag } from 'src/components/Tags';
import { ArticleDiscussion } from 'src/components/ArticleDiscussion';

const useStyles = createUseStyles({
  page: {
    margin: '20px 5% 0 5%',
    '& .ant-divider-horizontal': {
      borderTop: '2px solid rgba(0, 0, 0, 0.06)',
    },
  },
  pageHeader: {
    display: 'flex',
    fontSize: '1.5rem',
  },
  previousButton: {
    display: 'inline-block',
    width: '5%',
    textAlign: 'center',
    marginLeft: '20px',
    marginRight: '20px',
  },
  article: {
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '5px',
    boxShadow: '0 0 5px #ccc',
    marginBottom: '20px',
  },
  articleTitle: {
    marginBottom: '30px',
    textAlign: 'left',
    fontSize: '2rem',
    fontWeight: 'bolder',
    color: '#5c5c5c',
  },
  articleInformations: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1rem',
  },
  articleSourceInformation: {
    display: 'flex',
    marginBottom: '10px',
    '@media screen and (max-width: 800px)': {
      flexDirection: 'column',
      '& .firstBulletPoint': {
        display: 'inline-block',
        marginRight: '5px',
        marginLeft: '5px',
      },
      '& .lastBulletPoint': {
        display: 'none',
      },
    },
  },
  tags: {
    display: 'inline-block',
    width: '30%',
  },
  articleDescription: {
    fontSize: '1rem',
    marginBottom: '10px',
  },
  articleInfoPart: {
    display: 'inline-block',
    marginRight: '5px',
    '@media screen and (max-width: 800px)': {
      marginBottom: '10px',
    },
  },
  bulletPoint: {
    display: 'inline-block',
    marginRight: '5px',
    marginLeft: '5px',
  },
  firstBulletPoint: {
    display: 'none',
  },
});

const Article = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();

  const comments: Comment[] = useAppSelector(commentsSelector.selectAll);

  const [value, setValue] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { postId }: { postId: string } = useParams();
  const post: Post | undefined = postsSelector.selectById(
    store.getState(),
    postId,
  );

  const {
    user,
    users,
  }: { user: User | null; users: { [userId: string]: User } } = useAppSelector(
    ({ auth: { user, users } }) => ({ user, users }),
  );

  const [currentUser, setCurrentUser] = useState<User | null>();

  useEffect(() => {
    moment.locale(i18n.language);

    const fetchData = async () => {
      await dispatch(fetchPostByPostId(postId));
      await dispatch(fetchCommentsByPostId(postId));
      if (!user) {
        await dispatch(fetchCurrentUser());
      }
      const userPostToFetch = post ? (post.authorId as User)._id : '';
      const usersCommentsToFetch = comments.map((comment) =>
        comment.authorId ? comment.authorId : '',
      );
      const userIds = [userPostToFetch, ...usersCommentsToFetch];
      dispatch(
        findUsersByIds(
          userIds
            .filter((n, i) => userIds.indexOf(n) === i)
            .filter((id) => id !== ''),
        ),
      );
    };
    fetchData();

    return () => {
      dispatch(removeAllPosts());
      dispatch(removeAllComments());
      dispatch(removeAllTags());
    };
  }, []);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const likePostClick = (e: any) => {
    e.preventDefault();
    if (
      currentUser &&
      post?.likes.find((id) => {
        return id === currentUser._id;
      })
    ) {
      dispatch(removeLikeOfPost(postId));
    } else {
      dispatch(addLikeToPost(postId));
    }
  };

  const getBtnTypeLikePost = (post: Post): boolean => {
    return currentUser &&
      post?.likes.find((id) => {
        return id === currentUser._id;
      })
      ? true
      : false;
  };

  const goToPreviousPage = () => {
    history.goBack();
  };

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <div className={classes.previousButton}>
          <Tooltip title="Previous">
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowLeftOutlined />}
              size="large"
              onClick={() => {
                goToPreviousPage();
              }}
            />
          </Tooltip>
        </div>
      </div>
      <Divider />

      <div className={classes.article}>
        <div className={classes.articleTitle}>{post?.title}</div>
        <div className={classes.articleInformations}>
          <div className={classes.articleSourceInformation}>
            {post?.publisher || post?.url ? (
              <div className={classes.articleInfoPart}>
                <div className={`${classes.firstBulletPoint} firstBulletPoint`}>
                  &#8226;
                </div>
                <a href={post?.url}>
                  {post?.publisher ? post?.publisher : urlDomainName(post?.url)}
                </a>
              </div>
            ) : null}
            {post?.authorArticle ? (
              <div className={classes.articleInfoPart}>
                {post?.authorArticle && (
                  <div className={classes.bulletPoint}>&#8226;</div>
                )}
                {post?.authorArticle}
              </div>
            ) : null}
            {post?.datePublished ? (
              <div className={classes.articleInfoPart}>
                {post.datePublished && (
                  <div className={classes.bulletPoint}>&#8226;</div>
                )}
                {post.datePublished
                  ? `${t('article.published')} ${moment(
                      post?.datePublished,
                    ).fromNow()}`
                  : `${t('article.added')} ${moment(
                      post?.createdAt,
                    ).fromNow()}`}
              </div>
            ) : null}
            <div className={classes.articleInfoPart}>
              <div className={`${classes.bulletPoint} lastBulletPoint`}>
                &#8226;
              </div>
              <Button
                type={post && getBtnTypeLikePost(post) ? 'primary' : 'default'}
                icon={<LikeOutlined />}
                onClick={(e) => likePostClick(e)}
                size="small"
              >
                {t('article.like')} ({post?.likes.length})
              </Button>
            </div>
          </div>
          <div className={classes.tags}>
            {post &&
              (post.tags as Tag[]).map((tag) => (
                <CustomTag key={tag._id} _id={tag._id} name={tag.name} />
              ))}
          </div>
        </div>
        <Divider />
        <div className={classes.articleDescription}>{post?.description}</div>
      </div>
      <ArticleDiscussion
        post={post}
        comments={comments}
        users={users}
        postId={postId}
        currentUser={currentUser}
        value={value}
        setValue={setValue}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
    </div>
  );
};

export default Article;
